// Seed catalogue for Fancy Plants — exotic / unusual plants, tuned to an Irish
// midlands climate. In production this lives in Cosmos (container: products,
// pk: /category). Here it's local seed data so the storefront runs end-to-end.
//
// Images use a deterministic Unsplash source per slug so the grid is photo-led
// without bundling binaries. The "arrives" shot is part of the trust strategy.

const img = (id, w = 900) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=70`;

export const FILTERS = {
  light: [
    { value: "bright-indirect", label: "Bright indirect" },
    { value: "bright-direct", label: "Bright / direct" },
    { value: "low-light", label: "Low light tolerant" },
  ],
  placement: [
    { value: "indoor", label: "Indoor" },
    { value: "outdoor", label: "Hardy outdoor" },
  ],
  size: [
    { value: "small", label: "Small" },
    { value: "medium", label: "Medium" },
    { value: "large", label: "Statement" },
  ],
  season: [
    { value: "spring", label: "Spring" },
    { value: "summer", label: "Summer" },
    { value: "autumn", label: "Autumn" },
    { value: "all-year", label: "All year" },
  ],
};

export const PLANTS = [
  {
    id: "p-monstera-deliciosa",
    slug: "monstera-deliciosa",
    name: "Monstera Deliciosa",
    latinName: "Monstera deliciosa",
    category: "Statement Foliage",
    price: 38,
    compareAt: null,
    stock: 6,
    status: "active",
    blurb:
      "The Swiss cheese plant — big, glossy, fenestrated leaves that turn a corner of a damp Irish room into a glasshouse.",
    images: [img("1614594975525-e45190c55d0b"), img("1545241047-6083a3684587")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-indirect", placement: "indoor", petSafe: false, size: "large", season: "all-year" },
    careGuideId: "monstera-deliciosa",
    tags: ["easy", "fast-growing", "humidity-lover"],
  },
  {
    id: "p-zz-raven",
    slug: "zz-raven",
    name: "ZZ Raven",
    latinName: "Zamioculcas zamiifolia 'Raven'",
    category: "Statement Foliage",
    price: 32,
    compareAt: null,
    stock: 4,
    status: "active",
    blurb:
      "Near-black foliage that thrives on neglect and the low winter light of a midlands windowsill.",
    images: [img("1632900931939-9a1028f7cef6"), img("1614594895304-fe7116ac3b58")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "low-light", placement: "indoor", petSafe: false, size: "medium", season: "all-year" },
    careGuideId: "zz-raven",
    tags: ["low-light", "drought-tolerant", "beginner"],
  },
  {
    id: "p-calathea-orbifolia",
    slug: "calathea-orbifolia",
    name: "Calathea Orbifolia",
    latinName: "Goeppertia orbifolia",
    category: "Statement Foliage",
    price: 26,
    compareAt: 30,
    stock: 0,
    status: "active",
    blurb:
      "Silver-striped prayer-plant leaves the size of a side plate. Loves the damp — perfect for a steamy Irish bathroom.",
    images: [img("1777428904692-0ded971bdde5"), img("1648650219219-8970f28ee20c")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-indirect", placement: "indoor", petSafe: true, size: "medium", season: "all-year" },
    careGuideId: "calathea-orbifolia",
    tags: ["pet-safe", "humidity-lover", "statement"],
  },
  {
    id: "p-string-of-hearts",
    slug: "string-of-hearts",
    name: "String of Hearts",
    latinName: "Ceropegia woodii",
    category: "Trailing & Hanging",
    price: 16,
    compareAt: null,
    stock: 12,
    status: "active",
    blurb:
      "Delicate heart-shaped marbled leaves on trailing vines. A small plant with enormous charm.",
    images: [img("1771169254855-c95cddf241c2"), img("1771169255716-82c675a0fa45")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-indirect", placement: "indoor", petSafe: true, size: "small", season: "all-year" },
    careGuideId: "string-of-hearts",
    tags: ["pet-safe", "trailing", "beginner"],
  },
  {
    id: "p-tree-fern",
    slug: "irish-tree-fern",
    name: "Hardy Tree Fern",
    latinName: "Dicksonia antarctica",
    category: "Hardy Exotics",
    price: 89,
    compareAt: null,
    stock: 3,
    status: "active",
    blurb:
      "A genuinely exotic prehistoric silhouette that shrugs off Irish winters in a sheltered, damp garden corner.",
    images: [img("1758702160898-6f96d1db5b73"), img("1769607590924-e480e3e1940c")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-indirect", placement: "outdoor", petSafe: true, size: "large", season: "all-year" },
    careGuideId: "irish-tree-fern",
    tags: ["hardy", "outdoor", "statement", "irish-climate"],
  },
  {
    id: "p-phormium",
    slug: "phormium-flax",
    name: "New Zealand Flax",
    latinName: "Phormium tenax",
    category: "Hardy Exotics",
    price: 34,
    compareAt: null,
    stock: 7,
    status: "active",
    blurb:
      "Architectural bronze sword-leaves that bring an exotic, coastal-proof structure to an Irish border.",
    images: [img("1756777943283-aeaf60327888"), img("1756777943280-751451e3731d")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-direct", placement: "outdoor", petSafe: true, size: "large", season: "all-year" },
    careGuideId: "phormium-flax",
    tags: ["hardy", "outdoor", "drought-tolerant", "irish-climate"],
  },
  {
    id: "p-pilea",
    slug: "pilea-peperomioides",
    name: "Chinese Money Plant",
    latinName: "Pilea peperomioides",
    category: "Desk & Windowsill",
    price: 18,
    compareAt: null,
    stock: 15,
    status: "active",
    blurb:
      "Round pancake leaves on slender stems, and it throws off pups you can pass to friends. Endlessly shareable.",
    images: [img("1687810829027-4ccf6fdaa59e"), img("1746933075306-e1427e27b5ac")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-indirect", placement: "indoor", petSafe: true, size: "small", season: "spring" },
    careGuideId: "pilea-peperomioides",
    tags: ["pet-safe", "beginner", "propagates"],
  },
  {
    id: "p-bird-of-paradise",
    slug: "bird-of-paradise",
    name: "Bird of Paradise",
    latinName: "Strelitzia nicolai",
    category: "Statement Foliage",
    price: 65,
    compareAt: null,
    stock: 2,
    status: "active",
    blurb:
      "Huge paddle leaves that bring genuine tropical drama indoors. Give it the brightest spot in the house.",
    images: [img("1620741475287-bc63efcadca6"), img("1769701185833-9e96053b527e")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-direct", placement: "indoor", petSafe: false, size: "large", season: "summer" },
    careGuideId: "bird-of-paradise",
    tags: ["statement", "fast-growing", "tropical"],
  },
  {
    id: "p-hoya-carnosa",
    slug: "hoya-carnosa",
    name: "Hoya Carnosa",
    latinName: "Hoya carnosa",
    category: "Trailing & Hanging",
    price: 22,
    compareAt: null,
    stock: 9,
    status: "active",
    blurb:
      "Waxy trailing leaves and, in time, clusters of star-shaped scented flowers. A slow, rewarding houseplant.",
    images: [img("1773003135773-869724ab6083"), img("1771169255018-170ea17f4acd")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-indirect", placement: "indoor", petSafe: true, size: "medium", season: "summer" },
    careGuideId: "hoya-carnosa",
    tags: ["pet-safe", "trailing", "flowering"],
  },
  {
    id: "p-fatsia",
    slug: "fatsia-japonica",
    name: "Glossy Fatsia",
    latinName: "Fatsia japonica",
    category: "Hardy Exotics",
    price: 28,
    compareAt: null,
    stock: 5,
    status: "active",
    blurb:
      "Big glossy hand-shaped leaves that read as tropical but laugh at a damp, shady Irish garden.",
    images: [img("1616261253304-ef84f2611c36"), img("1711961817177-3af481fcfb5b")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "low-light", placement: "outdoor", petSafe: true, size: "large", season: "autumn" },
    careGuideId: "fatsia-japonica",
    tags: ["hardy", "outdoor", "shade", "irish-climate", "pet-safe"],
  },
  {
    id: "p-peperomia",
    slug: "peperomia-watermelon",
    name: "Watermelon Peperomia",
    latinName: "Peperomia argyreia",
    category: "Desk & Windowsill",
    price: 14,
    compareAt: null,
    stock: 11,
    status: "active",
    blurb:
      "Striped silver-and-green leaves that genuinely look like little watermelons. Compact and undemanding.",
    images: [img("1685721269025-d70a32c5bcd9"), img("1687810829027-4ccf6fdaa59e")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-indirect", placement: "indoor", petSafe: true, size: "small", season: "all-year" },
    careGuideId: "peperomia-watermelon",
    tags: ["pet-safe", "beginner", "compact"],
  },
  {
    id: "p-alocasia-zebrina",
    slug: "alocasia-zebrina",
    name: "Alocasia Zebrina",
    latinName: "Alocasia zebrina",
    category: "Statement Foliage",
    price: 44,
    compareAt: null,
    stock: 0,
    status: "active",
    blurb:
      "Arrow-shaped leaves held aloft on striking zebra-striped stems. A connoisseur's plant that rewards attention.",
    images: [img("1655382355596-7bf7012a92f1"), img("1655382355719-ddb73fce70a0")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-indirect", placement: "indoor", petSafe: false, size: "medium", season: "summer" },
    careGuideId: "alocasia-zebrina",
    tags: ["statement", "collector", "humidity-lover"],
  },
  {
    id: "p-blueberry-bush",
    slug: "blueberry-bush",
    name: "Blueberry Bush",
    latinName: "Vaccinium corymbosum 'Bluecrop'",
    category: "Edible Garden",
    price: 24,
    compareAt: null,
    stock: 10,
    status: "active",
    blurb:
      "A compact fruiting blueberry for acid-loving pots. White spring flowers, blue summer fruit, and fiery autumn colour.",
    images: [img("1757413010772-48cf15905808"), img("1528821128474-27f963b062bf")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-direct", placement: "outdoor", petSafe: true, size: "medium", season: "spring" },
    careGuideId: "blueberry-bush",
    tags: ["edible", "fruiting", "pollinator", "patio"],
  },
  {
    id: "p-strawberry-basket",
    slug: "strawberry-hanging-basket",
    name: "Strawberry Hanging Basket",
    latinName: "Fragaria x ananassa",
    category: "Edible Garden",
    price: 18,
    compareAt: null,
    stock: 14,
    status: "active",
    blurb:
      "A ready-to-grow basket of strawberries for a sunny wall, balcony or doorstep. Sweet fruit without needing a bed.",
    images: [img("1753187987252-8aed22dab9d1"), img("1571850216275-204ef8e771ff")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-direct", placement: "outdoor", petSafe: true, size: "small", season: "summer" },
    careGuideId: "strawberry-hanging-basket",
    tags: ["edible", "fruiting", "balcony", "pollinator"],
  },
  {
    id: "p-venus-flytrap",
    slug: "venus-flytrap",
    name: "Venus Flytrap",
    latinName: "Dionaea muscipula",
    category: "Carnivorous Plants",
    price: 15,
    compareAt: null,
    stock: 8,
    status: "active",
    blurb:
      "The famous snap-trap carnivore. Tiny, weird and brilliant for a bright windowsill with rainwater on hand.",
    images: [img("1755811351582-bba42eb8a9f1"), img("1517369428076-d7a1fc6acb9e")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-direct", placement: "indoor", petSafe: true, size: "small", season: "summer" },
    careGuideId: "venus-flytrap",
    tags: ["carnivorous", "collector", "windowsill", "rainwater"],
  },
  {
    id: "p-nepenthes-pitcher",
    slug: "nepenthes-pitcher-plant",
    name: "Nepenthes Pitcher Plant",
    latinName: "Nepenthes x ventrata",
    category: "Carnivorous Plants",
    price: 26,
    compareAt: null,
    stock: 5,
    status: "active",
    blurb:
      "Hanging green pitchers with red rims. A tropical carnivore that loves bright bathrooms and steady humidity.",
    images: [img("1778049004898-f5e5327e4f90"), img("1759585224329-567be6d20083")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-indirect", placement: "indoor", petSafe: true, size: "medium", season: "all-year" },
    careGuideId: "nepenthes-pitcher-plant",
    tags: ["carnivorous", "hanging", "humidity-lover", "collector"],
  },
  {
    id: "p-moroccan-mint",
    slug: "moroccan-mint",
    name: "Moroccan Mint",
    latinName: "Mentha spicata 'Moroccan'",
    category: "Edible Garden",
    price: 9,
    compareAt: null,
    stock: 18,
    status: "active",
    blurb:
      "Fresh mint for tea, salads and summer drinks. Keep it in its own pot and it will reward you for months.",
    images: [img("1534944645004-bf75556ce377")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-indirect", placement: "outdoor", petSafe: true, size: "small", season: "spring" },
    careGuideId: "moroccan-mint",
    tags: ["edible", "herb", "patio", "beginner"],
  },
  {
    id: "p-patio-cherry-tomato",
    slug: "patio-cherry-tomato",
    name: "Patio Cherry Tomato",
    latinName: "Solanum lycopersicum",
    category: "Edible Garden",
    price: 16,
    compareAt: null,
    stock: 20,
    status: "active",
    blurb:
      "A compact tomato for pots, patios and sunny balconies. Feed weekly once flowers appear and pick all summer.",
    images: [img("1723234870945-c4c4a9e2c683")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-direct", placement: "outdoor", petSafe: false, size: "medium", season: "summer" },
    careGuideId: "patio-cherry-tomato",
    tags: ["edible", "fruiting", "patio", "summer-crop"],
  },
  {
    id: "p-dwarf-olive-tree",
    slug: "dwarf-olive-tree",
    name: "Dwarf Olive Tree",
    latinName: "Olea europaea",
    category: "Mediterranean & Citrus",
    price: 42,
    compareAt: null,
    stock: 6,
    status: "active",
    blurb:
      "A silver-leaved olive for a warm patio or bright conservatory. Sculptural, drought-tolerant and quietly dramatic.",
    images: [img("1770914910209-48c07ab445e2")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-direct", placement: "outdoor", petSafe: true, size: "medium", season: "all-year" },
    careGuideId: "dwarf-olive-tree",
    tags: ["mediterranean", "patio", "drought-tolerant", "statement"],
  },
  {
    id: "p-calamondin-orange",
    slug: "calamondin-orange-tree",
    name: "Calamondin Orange Tree",
    latinName: "Citrofortunella microcarpa",
    category: "Mediterranean & Citrus",
    price: 45,
    compareAt: null,
    stock: 4,
    status: "active",
    blurb:
      "A small citrus tree with fragrant flowers and bright orange fruit. Best in a sunny conservatory or summer patio.",
    images: [img("1773919696914-68fe353ac28e")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-direct", placement: "indoor", petSafe: false, size: "medium", season: "all-year" },
    careGuideId: "calamondin-orange-tree",
    tags: ["citrus", "fruiting", "scented", "patio"],
  },
  {
    id: "p-green-chilli",
    slug: "green-chilli-plant",
    name: "Green Chilli Plant",
    latinName: "Capsicum annuum",
    category: "Edible Garden",
    price: 14,
    compareAt: null,
    stock: 12,
    status: "active",
    blurb:
      "A compact chilli plant for a sunny kitchen window or sheltered patio. Pick green, or leave them to ripen hotter.",
    images: [img("1650882142174-58d8fd758315")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-direct", placement: "indoor", petSafe: false, size: "small", season: "summer" },
    careGuideId: "green-chilli-plant",
    tags: ["edible", "spicy", "windowsill", "summer-crop"],
  },
  {
    id: "p-dwarf-banana",
    slug: "dwarf-banana",
    name: "Dwarf Banana",
    latinName: "Musa acuminata 'Dwarf Cavendish'",
    category: "Statement Foliage",
    price: 36,
    compareAt: null,
    stock: 4,
    status: "active",
    blurb:
      "Big paddle leaves without the full jungle footprint. A fast, fun statement plant for a bright warm room.",
    images: [img("1521687615313-6d3aadf452cf")],
    arrivesShot: img("1673573036163-a27b9a420dc5"),
    traits: { light: "bright-direct", placement: "indoor", petSafe: true, size: "large", season: "summer" },
    careGuideId: "dwarf-banana",
    tags: ["statement", "tropical", "fast-growing", "pet-safe"],
  },
];

export const CATEGORIES = [...new Set(PLANTS.map((p) => p.category))];

export const getPlantBySlug = (slug) => PLANTS.find((p) => p.slug === slug);
export const getRelated = (plant, n = 4) =>
  PLANTS.filter((p) => p.category === plant.category && p.id !== plant.id).slice(0, n);
